import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Eye, EyeOff, Apple } from "lucide-react";
import { toast } from "sonner";
import { BrandWord } from "@/components/BrandWord";
import { isNative, platform } from "@/lib/native";
import { api } from "@/lib/api";
import { passwordProblem, apiErrorMessage, PASSWORD_HINT } from "@/lib/passwordPolicy";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

export default function Login() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const initialRole = params.get("role") === "shop" ? "shop" : "owner";
  const [mode, setMode] = useState(initialMode);
  const [role, setRole] = useState(initialRole);
  const { loginWithJWT, registerWithJWT, user, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  // Referral program is hidden unless the backend REFERRALS_ENABLED flag is on.
  const { referralsEnabled } = useFeatureFlags();
  const [form, setForm] = useState({ email: "", password: "", name: "", shop_name: "" });
  // Required "18 or older" confirmation for new email signups (p1-alignment-nov2).
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  // Capture referral code from URL (?ref=MCAR-XXX). Stored until a successful
  // signup, then applied via /api/referrals/apply-code (best-effort, non-blocking).
  // Only while the referral program is enabled; otherwise ?ref= is ignored.
  useEffect(() => {
    if (!referralsEnabled) return;
    const ref = params.get("ref");
    if (ref) {
      try { localStorage.setItem("pending_ref_code", ref); } catch {}
    }
  }, [params, referralsEnabled]);

  useEffect(() => {
    // Auto-redirect ONLY when the user is fully authenticated (has a role).
    // Half-registered users (token in localStorage but role==null) should
    // see the login form so they can sign in as a different account, NOT
    // be force-routed to /onboarding which made tapping "Log In" feel
    // like it was opening Role Selection.
    if (!user) return;
    if (!user.role) return; // roleless: stay on login form
    if (user.role === "admin") {
      // Mirror auth_token -> admin_token so AdminAuthGuard recognises this
      // session as admin (Google Sign-In flow does the same).
      try {
        const t = localStorage.getItem("auth_token");
        if (t) localStorage.setItem("admin_token", t);
      } catch { /* ignore */ }
      navigate("/admin", { replace: true });
    } else navigate(user.role === "shop" ? "/shop" : "/owner", { replace: true });
  }, [user, navigate]);

  const handleApple = async () => {
    try {
      const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
      const result = await SignInWithApple.authorize({
        clientId: "com.shiftquote.app",
        redirectURI: "https://shiftquote.app/auth/callback",
        scopes: "email name",
      });
      const r = result?.response || {};
      const payload = {
        identity_token: r.identityToken,
        email: r.email || undefined,
        full_name: r.givenName || r.familyName
          ? { first: r.givenName, last: r.familyName }
          : undefined,
      };
      const resp = await api.post("/auth/apple", payload);
      localStorage.setItem("auth_token", resp.data.token);
      setUser(resp.data.user);
      toast.success("Welcome");
    } catch (err) {
      if (String(err?.message || err).toLowerCase().includes("cancel")) return; // user cancelled - silent
      toast.error(err?.response?.data?.detail || err?.message || "Apple sign-in failed");
    }
  };

  const handleGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // EXCEPTION: On Capacitor native, use the custom URL scheme (com.shiftquote.app://) instead of
    // Universal Links. SFSafariViewController on iOS cannot intercept Universal Links reliably,
    // but the OS always routes registered custom URL schemes back to the app. The custom scheme
    // is registered in Info.plist under CFBundleURLTypes. DeepLinkListener.jsx handles the
    // appUrlOpen event for both Universal Links and custom scheme callbacks identically.
    if (isNative()) {
      try {
        const { Browser } = await import("@capacitor/browser");
        const redirectUrl = "com.shiftquote.app://auth/callback";
        const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        // Open the OAuth flow in the system browser. When the provider redirects to
        // com.shiftquote.app://auth/callback, the OS routes the custom scheme URL back
        // to ShiftQuote and fires appUrlOpen. DeepLinkListener navigates to /auth/callback.
        await Browser.open({ url: authUrl, presentationStyle: "popover" });
      } catch (err) {
        toast.error("Could not open Google sign-in. Try email + password.");
      }
      return;
    }
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await loginWithJWT(form.email, form.password);
        toast.success("Welcome back");
        if (!u.role) navigate("/onboarding", { replace: true });
        else if (u.role === "admin") {
          try {
            const t = localStorage.getItem("auth_token");
            if (t) localStorage.setItem("admin_token", t);
          } catch { /* ignore */ }
          navigate("/admin", { replace: true });
        }
        else navigate(u.role === "shop" ? "/shop" : "/owner", { replace: true });
      } else {
        const pwErr = passwordProblem(form.password);
        if (pwErr) {
          toast.error(pwErr);
          return;
        }
        if (!ageConfirmed) {
          toast.error("Please confirm you are 18 or older to create an account.");
          return;
        }
        const payload = {
          age_confirmed: true,
          email: form.email,
          password: form.password,
          name: form.name,
          role,
          shop_name: role === "shop" ? form.shop_name : null,
          ...(referralsEnabled ? { referral_code: referralCode || null } : {}),
        };
        await registerWithJWT(payload);
        // Best-effort: apply pending referral code if any (referral program only)
        if (referralsEnabled) {
          try {
            const refCode = localStorage.getItem("pending_ref_code");
            if (refCode) {
              await api.post("/referrals/apply-code", { code: refCode }).catch(() => {});
              localStorage.removeItem("pending_ref_code");
            }
          } catch {}
        }
        // Registration now requires email verification — redirect to verify page
        toast.success("Account created! Check your email to verify.");
        navigate(`/verify-email?email=${encodeURIComponent(form.email)}`, { replace: true });
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = apiErrorMessage(err, "Failed");
      if (status === 403 && detail.toLowerCase().includes("verify")) {
        // Email not verified — redirect to verify-email page with resend option
        toast.error(detail);
        navigate(`/verify-email?email=${encodeURIComponent(form.email)}`, { replace: true });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col">
      <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-8 lg:px-12 py-8 flex justify-between items-center">
        {/* Prominent left-aligned Back arrow so the user can always return
            to the landing page without hunting for a small link. */}
        <Link
          to="/"
          className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm font-display tracking-wide transition-colors"
          data-testid="login-back-link"
          aria-label="Back to home"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back</span>
        </Link>
        <Link to="/" className="flex items-center gap-2.5" data-testid="login-home-link">
          <img src="/logo192.png" alt="ShiftQuote" className="w-8 h-8 object-contain shrink-0" />
          <BrandWord className="font-heading text-lg font-black tracking-tight" />
        </Link>
        {/* Spacer to keep the brand visually centered on wider screens. */}
        <div className="w-[68px] sm:w-[88px]" aria-hidden />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md border border-white/10 bg-[#121214] p-8 sm:p-10">
          <div className="label-eyebrow mb-4">{mode === "login" ? "ACCESS THE GARAGE" : "JOIN SHIFTQUOTE"}</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-8">
            {mode === "login" ? "Sign in" : "Create account"}
          </h1>

          {mode === "register" && (
            <div className="grid grid-cols-2 gap-0 mb-6 border border-white/10">
              <button
                type="button"
                onClick={() => setRole("owner")}
                className={`px-4 py-3 text-sm uppercase tracking-widest font-medium transition-colors ${role === "owner" ? "bg-[#FF3B30] text-white" : "bg-transparent text-zinc-400 hover:bg-white/5"}`}
                data-testid="role-owner-tab"
              >
                Car Owner
              </button>
              <button
                type="button"
                onClick={() => setRole("shop")}
                className={`px-4 py-3 text-sm uppercase tracking-widest font-medium transition-colors ${role === "shop" ? "bg-[#FF3B30] text-white" : "bg-transparent text-zinc-400 hover:bg-white/5"}`}
                data-testid="role-shop-tab"
              >
                Repair Shop
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <label className="label-eyebrow block mb-2">{role === "shop" ? "OWNER NAME" : "NAME"}</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-base" data-testid="register-name-input" placeholder="Jane Doe" />
                </div>
                {role === "shop" && (
                  <div>
                    <label className="label-eyebrow block mb-2">SHOP NAME</label>
                    <input required value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} className="input-base" data-testid="register-shop-name-input" placeholder="Main St Auto" />
                  </div>
                )}
              </>
            )}
            <div>
              <label className="label-eyebrow block mb-2">EMAIL</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value.trim() })}
                className="input-base"
                data-testid="email-input"
                placeholder="you@garage.com"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="email"
                inputMode="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-eyebrow">PASSWORD</label>
                {mode === "login" && (
                  <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-white underline underline-offset-4" data-testid="forgot-password-link">
                    Forgot?
                  </Link>
                )}
              </div>
              <div className="relative">
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input-base pr-12"
                  data-testid="password-input"
                  placeholder="Password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white p-1"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === "register" && (
                <p className="text-xs text-zinc-500 mt-2" data-testid="password-hint">{PASSWORD_HINT}</p>
              )}
            </div>
            {referralsEnabled && mode === "register" && role === "shop" && (
              <div>
                <label className="label-eyebrow text-zinc-500 mb-2 block">Referral Code (Optional)</label>
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase().trim())}
                  placeholder="e.g. JP924"
                  maxLength={12}
                  className="input-base w-full font-mono tracking-widest uppercase placeholder:normal-case placeholder:tracking-normal"
                  data-testid="register-referral-code"
                />
              </div>
            )}
            {mode === "register" && (
              <label className="flex items-start gap-3 text-sm text-zinc-300 cursor-pointer select-none" data-testid="register-age-label">
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                  required
                  className="mt-0.5 h-4 w-4 accent-[#FF9500]"
                  data-testid="register-age-checkbox"
                />
                <span>I am 18 or older.</span>
              </label>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="submit-auth-btn">
              {loading ? "..." : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-zinc-600 uppercase tracking-[0.3em]">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {isNative() && platform() === "ios" && (
            <button
              onClick={handleApple}
              className="btn-secondary w-full flex items-center justify-center gap-3 mb-3 bg-white text-black hover:bg-zinc-100 border-white"
              data-testid="apple-auth-btn"
            >
              <Apple className="w-4 h-4 fill-black" />
              Continue with Apple
            </button>
          )}

          {isNative() ? (
            <button onClick={handleGoogle} className="btn-secondary w-full flex items-center justify-center gap-3" data-testid="google-auth-btn-native">
              <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
              Continue with Google
            </button>
          ) : (
            <button onClick={handleGoogle} className="btn-secondary w-full flex items-center justify-center gap-3" data-testid="google-auth-btn">
              <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
              Continue with Google
            </button>
          )}

          <div className="mt-8 text-center text-sm text-zinc-500">
            {mode === "login" ? (
              <>New here? <button type="button" onClick={() => setMode("register")} className="text-white underline underline-offset-4" data-testid="switch-to-register">Create account</button></>
            ) : (
              <>Already have one? <button type="button" onClick={() => setMode("login")} className="text-white underline underline-offset-4" data-testid="switch-to-login">Sign in</button></>
            )}
          </div>
          <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-center gap-5 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
            <Link to="/privacy" className="hover:text-zinc-300 transition-colors" data-testid="login-privacy-link">Privacy</Link>
            <span className="text-zinc-800">.</span>
            <Link to="/terms" className="hover:text-zinc-300 transition-colors" data-testid="login-terms-link">Terms</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
